<div>
  <shared-confirm-dialog />

  <p-dialog
    header="Add New CAD"
    [(visible)]="addCadDialogVisible"
    [modal]="true"
    [style]="{ width: '50rem', maxWidth: '95vw' }"
    [appendTo]="'body'"
    [dismissableMask]="!uploadInProgress"
    [closable]="!uploadInProgress"
    (onHide)="closeAddCadDialog()"
  >
    <div class="h-72">
      @if (uploadInProgress) {
        <div class="flex flex-col items-center justify-center py-8 gap-3">
          <p-progressSpinner />
          <p class="text-muted-color">Uploading CAD file...</p>
        </div>
      } @else {
        <app-upload-cad
          [multiple]="false"
          (filesSelected)="onFilesSelected($event)"
        />
      }

      @if (uploadError) {
        <p class="text-sm text-red-500 mt-3">{{ uploadError }}</p>
      }
    </div>
  </p-dialog>

  <div class="mb-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
    <p-iconfield class="w-full sm:w-80">
      <p-inputicon styleClass="pi pi-search" />
      <input
        pInputText
        type="search"
        maxlength="255"
        [formControl]="searchQuery"
        placeholder="Search filename, owner, or customer…"
        aria-label="Search files"
        class="w-full"
      />
    </p-iconfield>
    <p-select
      [options]="companyFilterOptions"
      optionLabel="label"
      optionValue="value"
      [ngModel]="selectedCompanyFilter"
      (onChange)="onCompanyFilterChange($event.value)"
      appendTo="body"
      placeholder="All customers"
      [showClear]="true"
      styleClass="w-full sm:w-72"
    ></p-select>
    @if (isMouldingEnabled) {
      <label class="flex items-center gap-2 cursor-pointer">
        <p-checkbox
          inputId="include-moulding-cad"
          [binary]="true"
          [ngModel]="includeMouldingCad"
          (ngModelChange)="onIncludeMouldingCadChange($event)"
        />
        <span>Include Moulding CAD</span>
      </label>
    }
    <p-button
      class="sm:ml-auto"
      label="New CAD"
      icon="pi pi-plus"
      (onClick)="openAddCadDialog()"
    />
  </div>

  <div class="rounded-xl border border-surface-switch-200 shadow-sm">
    <!-- Header Row -->
    <div
      class="hidden lg:flex rounded-t-xl bg-surface-switch-100 items-center gap-4 p-4 text-sm font-bold text-surface-switch-600 uppercase"
    >
      <div class="flex-[2] min-w-0 flex items-center gap-2">
        <i class="pi pi-file opacity-0" aria-hidden="true"></i>
        <span>Filename</span>
      </div>
      <div class="flex-1 min-w-0">Owner</div>
      <div class="flex-1 min-w-0">Customer</div>
      <div class="flex-1 min-w-0">Uploaded</div>
      <div class="flex-[2] min-w-0">Linked To</div>
      <div class="w-40 text-left">Actions</div>
    </div>

    @if (loading) {
      <div class="p-8 text-center text-muted-color">
        <i class="pi pi-spinner pi-spin text-2xl"></i>
        <p class="mt-2">Loading files...</p>
      </div>
    } @else if (loadError) {
      <div class="p-8 text-center text-muted-color">
        <i class="pi pi-exclamation-triangle text-4xl mb-2"></i>
        <p class="mb-4">{{ loadError }}</p>
        <p-button
          label="Retry"
          severity="secondary"
          [outlined]="true"
          (onClick)="loadFiles(true)"
        />
      </div>
    } @else if (files.length === 0 && activeSearch) {
      <div class="p-8 text-center text-muted-color">
        <i class="pi pi-search text-4xl mb-2"></i>
        <p>No files match your search.</p>
      </div>
    } @else if (files.length === 0) {
      <div class="p-8 text-center text-muted-color">
        <i class="pi pi-inbox text-4xl mb-2"></i>
        <p>No files found.</p>
      </div>
    } @else {
      <div class="flex flex-col">
        @for (file of files; track file.id) {
          <div
            class="lg:p-4 border border-surface-switch-200 lg:flex lg:items-center lg:gap-4 bg-surface-switch-50 hover:bg-surface-switch-100 transition-colors duration-200 cursor-pointer"
            role="button"
            tabindex="0"
            [attr.aria-label]="'View file ' + file.filename"
            (click)="openViewDialog(file)"
            (keydown.enter)="
              $event.target === $event.currentTarget && openViewDialog(file)
            "
          >
            <!-- Mobile Layout -->
            <div class="flex flex-col gap-3 p-4 lg:hidden">
              <div class="flex items-center justify-between">
                <span class="font-semibold text-lg truncate">{{
                  file.filename
                }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-muted-color font-semibold">Owner</span>
                <span>{{ file.owner }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-muted-color font-semibold">Customer</span>
                <span>{{ file.company_name || "None" }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-muted-color font-semibold">Uploaded</span>
                <span>{{ file.created_date | date: "medium" }}</span>
              </div>
              @if (
                file.linked_quote_items.length > 0 ||
                file.linked_moulding_flows.length > 0
              ) {
                <div>
                  <span class="text-muted-color font-semibold">Linked To</span>
                  <div class="flex flex-wrap gap-1 mt-1">
                    @for (
                      item of file.linked_quote_items;
                      track item.persistent_id
                    ) {
                      <span
                        [pTooltip]="getLinkedQuoteItemTooltip(item)"
                        tooltipPosition="top"
                      >
                        <button
                          (click)="
                            $event.stopPropagation(); navigateToQuote(item)
                          "
                          class="text-xs px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                        >
                          {{ item.quote_code_number }}
                        </button>
                      </span>
                    }
                    @for (flow of file.linked_moulding_flows; track flow.id) {
                      <button
                        (click)="
                          $event.stopPropagation(); navigateToMouldingFlow(flow)
                        "
                        class="text-xs px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                      >
                        {{ flow.name }}
                      </button>
                    }
                  </div>
                </div>
              }
              <div
                class="flex justify-end gap-2"
                (click)="$event.stopPropagation()"
              >
                <p-button
                  icon="pi pi-pencil"
                  severity="info"
                  [text]="true"
                  (onClick)="openRenameDialog(file)"
                  pTooltip="Rename"
                  tooltipPosition="top"
                  ariaLabel="Rename file"
                />
                <p-button
                  icon="pi pi-download"
                  severity="secondary"
                  [text]="true"
                  (onClick)="downloadFile(file)"
                  pTooltip="Download"
                  tooltipPosition="top"
                />
                <span
                  [pTooltip]="file.delete_blocked_reason ?? 'Delete'"
                  tooltipPosition="top"
                >
                  <p-button
                    icon="pi pi-trash"
                    severity="danger"
                    [text]="true"
                    [disabled]="!file.can_delete"
                    (onClick)="confirmDelete(file)"
                  />
                </span>
              </div>
            </div>

            <!-- Desktop Layout -->
            <div class="hidden lg:flex lg:flex-[2] min-w-0 items-center gap-2">
              <i class="pi pi-file text-muted-color"></i>
              <span class="truncate font-medium">{{ file.filename }}</span>
            </div>
            <div class="hidden lg:block flex-1 min-w-0 truncate text-sm">
              {{ file.owner }}
            </div>
            <div
              class="hidden lg:block flex-1 min-w-0 truncate text-sm text-muted-color"
            >
              {{ file.company_name || "None" }}
            </div>
            <div
              class="hidden lg:block flex-1 min-w-0 truncate text-sm text-muted-color"
            >
              {{ file.created_date | date: "medium" }}
            </div>
            <div class="hidden lg:flex flex-[2] min-w-0 flex-wrap gap-1">
              @if (
                file.linked_quote_items.length === 0 &&
                file.linked_moulding_flows.length === 0
              ) {
                <span class="text-sm text-muted-color italic">None</span>
              }
              @for (item of file.linked_quote_items; track item.persistent_id) {
                <span
                  [pTooltip]="getLinkedQuoteItemTooltip(item)"
                  tooltipPosition="top"
                >
                  <button
                    (click)="$event.stopPropagation(); navigateToQuote(item)"
                    class="text-xs px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                  >
                    {{ item.quote_code_number }}
                  </button>
                </span>
              }
              @for (flow of file.linked_moulding_flows; track flow.id) {
                <button
                  (click)="
                    $event.stopPropagation(); navigateToMouldingFlow(flow)
                  "
                  class="text-xs px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                >
                  {{ flow.name }}
                </button>
              }
            </div>
            <div
              class="hidden lg:flex w-40 justify-start gap-1"
              (click)="$event.stopPropagation()"
            >
              <p-button
                icon="pi pi-pencil"
                severity="info"
                [text]="true"
                (onClick)="openRenameDialog(file)"
                pTooltip="Rename"
                tooltipPosition="top"
                ariaLabel="Rename file"
              />
              <p-button
                icon="pi pi-download"
                severity="secondary"
                [text]="true"
                (onClick)="downloadFile(file)"
                pTooltip="Download"
                tooltipPosition="top"
              />
              <span
                [pTooltip]="file.delete_blocked_reason ?? 'Delete'"
                tooltipPosition="top"
              >
                <p-button
                  icon="pi pi-trash"
                  severity="danger"
                  [text]="true"
                  [disabled]="!file.can_delete"
                  (onClick)="confirmDelete(file)"
                />
              </span>
            </div>
          </div>
        }
      </div>
    }
  </div>

  <div class="mt-4 flex justify-center">
    <p-paginator
      [rows]="rows"
      [first]="first"
      [totalRecords]="totalRecords"
      [rowsPerPageOptions]="[20, 50, 100]"
      [showCurrentPageReport]="true"
      currentPageReportTemplate="{first} - {last} of {totalRecords}"
      (onPageChange)="onPageChange($event)"
    />
  </div>

  <!-- Rename File Dialog -->
  <p-dialog
    header="Rename File"
    [visible]="renameTarget !== null"
    (visibleChange)="!$event && closeRenameDialog()"
    [modal]="true"
    [style]="{ width: '30rem', maxWidth: '95vw' }"
    [appendTo]="'body'"
    [dismissableMask]="!renameInProgress"
    [closable]="!renameInProgress"
  >
    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-2">
        <input
          pInputText
          type="text"
          maxlength="250"
          class="min-w-0 flex-1"
          aria-label="File name"
          [formControl]="renameControl"
          (keydown.enter)="submitRename()"
        />
        <span class="text-muted-color" data-testid="rename-extension">{{
          renameExtension
        }}</span>
      </div>
      @if (renameError) {
        <p class="text-sm text-red-500 !m-0">{{ renameError }}</p>
      }
      <div class="flex justify-end gap-2">
        <p-button
          label="Cancel"
          severity="secondary"
          [text]="true"
          [disabled]="renameInProgress"
          (onClick)="closeRenameDialog()"
        />
        <p-button
          label="Save"
          [loading]="renameInProgress"
          [disabled]="!renameControl.value.trim()"
          (onClick)="submitRename()"
        />
      </div>
    </div>
  </p-dialog>

  <!-- View File Dialog -->
  <p-dialog
    [header]="selectedFile?.filename ?? 'View File'"
    [(visible)]="viewDialogVisible"
    [modal]="true"
    [style]="{ width: '70vw', maxWidth: '70rem' }"
    [appendTo]="'body'"
    [dismissableMask]="true"
    [closable]="true"
    (onHide)="closeViewDialog()"
  >
    @if (selectedFile) {
      @if (viewDialogLoading) {
        <div class="flex justify-center items-center py-12">
          <p-progressSpinner />
        </div>
      } @else if (loadedCadObjects.length > 0) {
        <div class="flex min-h-[65vh] flex-col gap-4 lg:flex-row">
          <div class="min-h-0 flex-1">
            <app-cad-viewer
              [cadObjects]="selectedCadObject ? [selectedCadObject] : []"
              [mouldSolution]="selectedMouldSolution()"
            ></app-cad-viewer>
          </div>
          <aside
            class="max-h-[65vh] overflow-y-auto rounded-lg border border-surface-switch-200 bg-surface-switch-0 p-4 lg:w-80"
          >
            <h3
              class="text-sm font-semibold uppercase tracking-wide text-surface-switch-500 !mt-2"
            >
              CAD Properties
            </h3>
            @if (selectedCadObject) {
              <app-cad-properties [cadObject]="selectedCadObject" />
            }

            <h3
              class="text-sm font-semibold uppercase tracking-wide text-surface-switch-500 !mt-6"
            >
              Mould Solutions
            </h3>
            @if (mouldSolutionsLoading()) {
              <div class="flex justify-center py-4">
                <i class="pi pi-spinner pi-spin text-muted-color"></i>
              </div>
            } @else if (mouldSolutions().length === 0) {
              <p class="text-sm text-muted-color">No mould solutions found.</p>
            } @else {
              <div class="flex flex-col gap-2">
                @for (solution of mouldSolutions(); track solution.id) {
                  <button
                    type="button"
                    class="flex flex-col gap-1 rounded-md border p-2 text-left text-sm transition-colors hover:bg-surface-switch-100"
                    [class.border-primary]="
                      selectedMouldSolution()?.id === solution.id
                    "
                    [class.border-surface-switch-200]="
                      selectedMouldSolution()?.id !== solution.id
                    "
                    (click)="toggleMouldSolution(solution)"
                  >
                    <div class="flex items-center justify-between gap-2">
                      <span class="font-medium"
                        >#{{ solution.id }} · {{ solution.solution_of }} ·
                        {{ solution.side }}</span
                      >
                      <span class="text-muted-color">{{
                        solution.score | number: "1.0-3"
                      }}</span>
                    </div>
                    <div class="text-xs text-muted-color">
                      {{ solution.is_closed ? "Closed" : "Open" }}
                      @if (solution.is_incomplete) {
                        · Incomplete
                      }
                      @if (solution.is_laminar_pair) {
                        · Laminar pair
                      }
                      @if (solution.refined_pieces.length > 0) {
                        · Refined
                      }
                    </div>
                    <div class="flex flex-col gap-1">
                      @for (
                        piece of displayedMouldPieces(solution);
                        track piece.id;
                        let pieceIndex = $index
                      ) {
                        <div class="flex items-center gap-2 text-xs">
                          <span
                            class="inline-block h-3 w-3 shrink-0 rounded-sm"
                            [style.background-color]="
                              mouldPieceColor(pieceIndex)
                            "
                          ></span>
                          Piece #{{ piece.id }} ·
                          {{ piece.object_face_indices.length }} faces
                        </div>
                      }
                    </div>
                  </button>
                }
              </div>
            }
          </aside>
        </div>
      } @else {
        <div class="text-center text-muted-color py-12">
          <p>No viewable CAD objects in this file.</p>
        </div>
      }
    }
  </p-dialog>
</div>
